import { useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { RefreshCw } from 'lucide-react'
import { formatearFecha, formatearFechaHora } from '@/lib/fecha'
import { formatearRNC } from '@/lib/rnc'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { LineaEsqueleto } from '@/ui/Esqueleto'
import { InsigniaDiscreta, InsigniaEstado } from '@/ui/InsigniaEstado'
import { LineaDeTiempo, type EventoLinea } from '@/ui/LineaDeTiempo'
import { ENCF, FechaCorta, FechaHora, RNC } from '@/ui/Numero'
import { PanelLateral } from '@/ui/PanelLateral'
import { TablaDatos } from '@/ui/tabla/TablaDatos'
import { useFiltrosEnUrl } from '@/ui/tabla/filtrosEnUrl'
import { traducirErrorApi } from '@/dominio/documentos/errores'
import { nombreTipoECF } from '@/dominio/documentos/tipos'
import {
  PRESENTACION_CANAL,
  PRESENTACION_ENTREGA,
  TEXTO_ACCION,
  bordeDeFilaEntrega,
  etiquetaCanal,
  nombreReceptor,
  presentacionDeEntrega,
  salidaMotivoAcuse,
  sePuedeReintentar,
  textoAcuse,
  textoMotivoAcuse,
  tipoComoTexto,
  tonoDelAcuse,
  type CanalEntrega,
  type EstadoEntrega,
  type FilaEntrega,
} from '@/dominio/entregas/tipos'
import {
  useBandejaDeEntregas,
  useEntregasDeDocumento,
  useReintentarEntrega,
} from '@/dominio/entregas/useEntregas'
import { Dato, EncabezadoPantalla } from './-pantalla'

/**
 * Bandeja de entregas al receptor.
 *
 * **Transmitir a DGII y entregarle el comprobante al comprador son dos
 * obligaciones distintas.** El e-CF va a DGII por el motor de emisión; al
 * comprador que es receptor electrónico hay que entregárselo en la URL que él
 * publica en el directorio. Esta pantalla enseña la segunda, que hasta ahora no
 * se veía en ninguna parte.
 *
 * Lo que la distingue, y lo único que de verdad decide si sirve:
 *
 *   **`no_entregable` no es un error.** Es el desenlace correcto del comprador
 *   que no es receptor electrónico —el caso mayoritario— y lo que le toca es la
 *   representación impresa. Va en gris, con la palabra "correcto" al lado y la
 *   explicación escrita. Si esta pantalla asusta con cientos de «fallos» que no
 *   lo son, nadie la mira, y entonces las entregas que de verdad fallaron se
 *   pierden entre el ruido
 *
 *   **rechazo y fallo no se arreglan igual.** `rechazada` es el receptor
 *   diciendo que no acepta el comprobante, con su motivo: se corrige. `fallida`
 *   es que no se pudo hablar con él: se reintenta. La pantalla dice cuál toca y
 *   solo ofrece el botón donde tiene sentido
 *
 *   **el acuse va invertido**: el cero es "recibido" y es el bueno
 */
export interface BusquedaEntregas {
  estado?: string
  canal?: string
  desde?: string
  hasta?: string
  receptor?: string
  texto?: string
  /** `'si'` enseña todos los intentos; vacío, solo el último de cada documento. */
  historico?: string
}

export const Route = createFileRoute('/entregas')({
  validateSearch: (busqueda: Record<string, unknown>): BusquedaEntregas => ({
    estado: typeof busqueda.estado === 'string' ? busqueda.estado : undefined,
    canal: typeof busqueda.canal === 'string' ? busqueda.canal : undefined,
    desde: typeof busqueda.desde === 'string' ? busqueda.desde : undefined,
    hasta: typeof busqueda.hasta === 'string' ? busqueda.hasta : undefined,
    receptor: typeof busqueda.receptor === 'string' ? busqueda.receptor : undefined,
    texto: typeof busqueda.texto === 'string' ? busqueda.texto : undefined,
    historico: typeof busqueda.historico === 'string' ? busqueda.historico : undefined,
  }),
  component: PantallaEntregas,
})

/**
 * El orden del desplegable no es alfabético: arriba lo que pide acción.
 * `no_entregable` va al final porque es el desenlace que no pide nada.
 */
const ESTADOS_FILTRABLES: readonly EstadoEntrega[] = [
  'fallida',
  'rechazada',
  'entregada',
  'no_entregable',
]

const CANALES_FILTRABLES: readonly CanalEntrega[] = [
  'receptor_electronico',
  'correo',
  'enlace',
  'papel',
]

function PantallaEntregas() {
  const { filtros, aplicar, limpiar, hayFiltrosActivos } = useFiltrosEnUrl({
    estado: '',
    canal: '',
    desde: '',
    hasta: '',
    receptor: '',
    texto: '',
    historico: '',
  })

  const [pagina, setPagina] = useState(1)
  const [tamano, setTamano] = useState(25)
  const [detalle, setDetalle] = useState<FilaEntrega | null>(null)

  const historico = filtros.historico === 'si'

  const { consulta, resumen } = useBandejaDeEntregas(
    {
      estado: filtros.estado === '' ? undefined : (filtros.estado as EstadoEntrega),
      canal: filtros.canal === '' ? undefined : (filtros.canal as CanalEntrega),
      desde: filtros.desde === '' ? undefined : filtros.desde,
      hasta: filtros.hasta === '' ? undefined : filtros.hasta,
      receptor: filtros.receptor === '' ? undefined : filtros.receptor,
      texto: filtros.texto === '' ? undefined : filtros.texto,
      // Por omisión el servidor devuelve el último intento de cada documento.
      // Solo se manda cuando es `true`: un comprobante que falló tres veces y se
      // entregó a la cuarta no son cuatro problemas, es cero.
      historico: historico ? true : undefined,
    },
    pagina,
    tamano,
  )

  const columnas = useMemo<ColumnDef<FilaEntrega, unknown>[]>(
    () => [
      {
        id: 'creadoEl',
        accessorFn: (fila) => fila.creadoEl,
        header: 'Intento',
        enableSorting: false,
        meta: {
          etiqueta: 'Fecha del intento',
          numerico: true,
          ancho: '150px',
          exportar: (f) => formatearFechaHora(f.creadoEl),
        },
        cell: ({ row }) => <FechaHora valor={row.original.creadoEl} />,
      },
      {
        id: 'encf',
        accessorFn: (fila) => fila.eNCF ?? '',
        header: 'e-NCF',
        enableSorting: false,
        meta: { etiqueta: 'e-NCF', numerico: true, ancho: '165px', exportar: (f) => f.eNCF },
        cell: ({ row }) => <ENCF valor={row.original.eNCF} />,
      },
      {
        id: 'tipo',
        accessorFn: (fila) => fila.tipo,
        header: 'Tipo',
        enableSorting: false,
        meta: {
          etiqueta: 'Tipo',
          numerico: true,
          ancho: '60px',
          exportar: (f) => tipoComoTexto(f.tipo),
        },
        cell: ({ row }) => (
          <span className="mono text-gris-700" title={nombreTipoECF(tipoComoTexto(row.original.tipo))}>
            {tipoComoTexto(row.original.tipo) ?? '—'}
          </span>
        ),
      },
      {
        id: 'receptor',
        accessorFn: (fila) => fila.razonSocialReceptor ?? fila.rncReceptor ?? '',
        header: 'Receptor',
        enableSorting: false,
        meta: { etiqueta: 'Receptor', exportar: (f) => nombreReceptor(f) },
        cell: ({ row }) => (
          <span className="block truncate text-gris-900" title={nombreReceptor(row.original)}>
            {nombreReceptor(row.original)}
          </span>
        ),
      },
      {
        id: 'rnc',
        accessorFn: (fila) => fila.rncReceptor ?? '',
        header: 'RNC',
        enableSorting: false,
        meta: {
          etiqueta: 'RNC',
          numerico: true,
          ancho: '130px',
          exportar: (f) => formatearRNC(f.rncReceptor),
        },
        cell: ({ row }) => <RNC valor={row.original.rncReceptor} />,
      },
      {
        id: 'canal',
        accessorFn: (fila) => fila.canal,
        header: 'Canal',
        enableSorting: false,
        meta: {
          etiqueta: 'Canal',
          ancho: '155px',
          exportar: (f) => etiquetaCanal(f.canal),
        },
        cell: ({ row }) => (
          <span className="truncate text-gris-700" title={PRESENTACION_CANAL[row.original.canal].nota}>
            {etiquetaCanal(row.original.canal)}
          </span>
        ),
      },
      {
        id: 'numeroIntento',
        accessorFn: (fila) => fila.intento,
        header: 'Nº',
        enableSorting: false,
        meta: {
          etiqueta: 'Nº de intento',
          numerico: true,
          ancho: '48px',
          exportar: (f) => f.intento,
        },
        cell: ({ row }) => <span className="numero text-gris-700">{row.original.intento}</span>,
      },
      {
        id: 'estado',
        accessorFn: (fila) => fila.estado,
        header: 'Desenlace',
        enableSorting: false,
        meta: {
          etiqueta: 'Desenlace',
          ancho: '250px',
          exportar: (f) => PRESENTACION_ENTREGA[f.estado].etiqueta,
        },
        cell: ({ row }) => {
          const presentacion = presentacionDeEntrega(row.original.estado)
          return (
            // `no_entregable` sale en gris con "correcto" al lado. No es un
            // fallo: es lo que le toca al comprador que no es receptor
            // electrónico, y pintarlo de rojo sería mentirle al usuario.
            <InsigniaEstado
              tono={presentacion.tono}
              aclaracion={presentacion.aclaracion}
              titulo={`${presentacion.explicacion} ${TEXTO_ACCION[presentacion.accion]}`}
            >
              {presentacion.etiqueta}
            </InsigniaEstado>
          )
        },
      },
    ],
    [],
  )

  const errorTraducido = consulta.isError ? traducirErrorApi(consulta.error) : null

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <EncabezadoPantalla
        titulo="Entregas"
        descripcion="Transmitir a DGII y entregarle el comprobante al comprador son dos obligaciones distintas. Aquí está la segunda: lo que le llegó a cada receptor electrónico."
      />

      {/*
        La cifra que evita que la bandeja se lea como una lista de averías.
        Dice "de esta página" porque se cuenta sobre las filas cargadas y no
        sobre el total del servidor: una cifra que parece global y no lo es se
        descubre el día que alguien la compara con el listado.
      */}
      {resumen.sinReceptor > 0 ? (
        <Banner
          tono="neutral"
          titulo={`${resumen.sinReceptor} de esta página no tienen receptor electrónico, y eso no es un fallo.`}
        >
          Esos compradores no publican servicio de recepción en el directorio de DGII, así que no hay
          a quién entregarle el XML: lo que les toca es la representación impresa.{' '}
          {resumen.requierenAtencion > 0
            ? `Lo que pide atención son ${resumen.requierenAtencion}: las rechazadas y las fallidas.`
            : 'Ninguna entrega de esta página pide atención.'}
        </Banner>
      ) : null}

      <TablaDatos<FilaEntrega>
        idTabla="entregas"
        columnas={columnas}
        datos={consulta.data?.filas ?? []}
        obtenerId={(fila) => fila.id}
        cargando={consulta.isLoading}
        error={
          errorTraducido === null
            ? null
            : {
                titulo: errorTraducido.titulo,
                contexto: errorTraducido.contexto,
                detalleTecnico: errorTraducido.detalleTecnico,
              }
        }
        onReintentar={() => void consulta.refetch()}
        vacio={{
          variante: 'primera-vez',
          mensaje:
            'Todavía no hay entregas registradas. Aparecen solas: cada comprobante que se emite se intenta entregar a su comprador.',
        }}
        vacioFiltrado={{
          variante: 'sin-resultados',
          mensaje: describirFiltroVacio(filtros),
          accion: (
            <Boton variante="secundario" tamano="menor" onClick={limpiar}>
              Limpiar filtros
            </Boton>
          ),
        }}
        paginacion={{ pagina, tamano, total: consulta.data?.total ?? 0 }}
        onPaginacionChange={(siguiente) => {
          setPagina(siguiente.pagina)
          setTamano(siguiente.tamano)
        }}
        nombreExportacion="entregas"
        hayFiltrosActivos={hayFiltrosActivos}
        onLimpiarFiltros={limpiar}
        filtros={
          <>
            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Desenlace
              <select
                value={filtros.estado}
                onChange={(evento) => {
                  setPagina(1)
                  aplicar({ estado: evento.target.value })
                }}
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                {ESTADOS_FILTRABLES.map((estado) => (
                  <option key={estado} value={estado}>
                    {PRESENTACION_ENTREGA[estado].etiqueta}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Canal
              <select
                value={filtros.canal}
                onChange={(evento) => {
                  setPagina(1)
                  aplicar({ canal: evento.target.value })
                }}
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                {CANALES_FILTRABLES.map((canal) => (
                  <option key={canal} value={canal}>
                    {PRESENTACION_CANAL[canal].etiqueta}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Desde
              <input
                type="date"
                value={filtros.desde}
                onChange={(evento) => {
                  setPagina(1)
                  aplicar({ desde: evento.target.value })
                }}
                className="numero h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-gris-900"
              />
            </label>

            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Hasta
              <input
                type="date"
                value={filtros.hasta}
                onChange={(evento) => {
                  setPagina(1)
                  aplicar({ hasta: evento.target.value })
                }}
                className="numero h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-gris-900"
              />
            </label>

            <input
              type="search"
              value={filtros.receptor}
              onChange={(evento) => {
                setPagina(1)
                aplicar({ receptor: evento.target.value })
              }}
              placeholder="RNC del receptor"
              aria-label="Filtrar por RNC del receptor"
              className="numero h-[var(--alto-control-menor)] w-[150px] rounded-md border border-gris-500 bg-white px-2 text-gris-900"
            />

            <input
              type="search"
              value={filtros.texto}
              onChange={(evento) => {
                setPagina(1)
                aplicar({ texto: evento.target.value })
              }}
              placeholder="e-NCF, nombre o mensaje"
              aria-label="Buscar en las entregas"
              className="h-[var(--alto-control-menor)] w-[220px] rounded-md border border-gris-500 bg-white px-2 text-cuerpo text-gris-900"
            />

            {/*
              Por omisión se ve el último intento de cada documento. El
              histórico es un interruptor y no el estado normal: si cada
              reintento contara como una fila, un documento que acabó entregado
              al cuarto intento se leería como cuatro problemas.
            */}
            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              <input
                type="checkbox"
                checked={historico}
                onChange={(evento) => {
                  setPagina(1)
                  aplicar({ historico: evento.target.checked ? 'si' : '' })
                }}
                className="size-4 accent-[var(--color-marca)]"
              />
              Ver todos los intentos
            </label>
          </>
        }
        onFilaClick={(fila) => setDetalle(fila)}
        segundaLinea={(fila) => segundaLineaDeEntrega(fila)}
        bordeFila={(fila) => bordeDeFilaEntrega(fila.estado)}
      />

      <PanelDeEntrega entrega={detalle} onCerrar={() => setDetalle(null)} />
    </div>
  )
}

/**
 * La segunda línea, solo donde aporta.
 *
 * Un rechazo enseña su motivo **sin tener que entrar**, y una fallida enseña
 * qué contestó el servidor. `no_entregable` no lleva segunda línea a propósito:
 * es el desenlace mayoritario y repetir la misma frase en cientos de filas
 * convierte la explicación en ruido. Su explicación está en la insignia, en su
 * `title` y en el aviso de arriba, que es donde se lee una vez.
 */
function segundaLineaDeEntrega(fila: FilaEntrega) {
  if (fila.estado === 'rechazada') {
    const motivo = textoMotivoAcuse(fila.acuseMotivo)
    const salida = salidaMotivoAcuse(fila.acuseMotivo)
    const texto =
      motivo === null
        ? (fila.mensaje ?? 'El receptor no dijo por qué. Se corrige, no se reintenta.')
        : `${motivo}. ${salida ?? 'Se corrige, no se reintenta.'}`
    return (
      <span className="text-rojo" title={texto}>
        {texto}
      </span>
    )
  }

  if (fila.estado === 'fallida') {
    const detalle = [
      fila.httpStatus === null ? null : `HTTP ${fila.httpStatus}`,
      fila.mensaje ?? 'No se pudo hablar con el servicio del receptor.',
      sePuedeReintentar(fila) ? 'Se reintenta.' : 'El servidor no lo da por reintentable.',
    ]
      .filter((parte): parte is string => parte !== null)
      .join(' · ')
    return (
      <span className="text-gris-600" title={detalle}>
        {detalle}
      </span>
    )
  }

  return null
}

function describirFiltroVacio(filtros: {
  estado: string
  canal: string
  desde: string
  hasta: string
}): string {
  if (filtros.estado !== '') {
    const presentacion = PRESENTACION_ENTREGA[filtros.estado as EstadoEntrega]
    return `Ninguna entrega con desenlace ${presentacion?.etiqueta.toLowerCase() ?? filtros.estado}.`
  }
  if (filtros.canal !== '') {
    return `Ninguna entrega por ${etiquetaCanal(filtros.canal as CanalEntrega).toLowerCase()}.`
  }
  if (filtros.desde !== '' && filtros.hasta !== '') {
    return `Ninguna entrega entre el ${formatearFecha(filtros.desde)} y el ${formatearFecha(filtros.hasta)}.`
  }
  return 'Ninguna entrega con los filtros aplicados.'
}

/* ---------------------------------------------------------------------- */
/* Detalle                                                                 */
/* ---------------------------------------------------------------------- */

/**
 * Detalle en panel lateral: **la bandeja queda visible detrás**, que es el
 * punto de usar un panel y no una página nueva.
 *
 * Enseña el desenlace con su salida escrita, el acuse del receptor traducido, y
 * **todos los intentos del documento**, que es lo que explica por qué una
 * entrega acabó como acabó.
 */
function PanelDeEntrega({ entrega, onCerrar }: { entrega: FilaEntrega | null; onCerrar: () => void }) {
  const reintentar = useReintentarEntrega()
  const intentos = useEntregasDeDocumento(entrega?.documentoId ?? null)

  const presentacion = entrega === null ? null : presentacionDeEntrega(entrega.estado)
  const puedeReintentar = entrega !== null && sePuedeReintentar(entrega)

  const eventos = useMemo<EventoLinea[]>(() => {
    const lista = intentos.data
    if (lista === undefined) return []
    return lista.map((intento) => {
      const suPresentacion = presentacionDeEntrega(intento.estado)
      return {
        id: intento.id,
        etiqueta: `Intento ${intento.intento} · ${suPresentacion.etiqueta}`,
        momento: intento.creadoEl,
        detalle: <FechaCorta valor={intento.creadoEl} className="text-gris-600" />,
        tono: suPresentacion.tono,
        // La respuesta del receptor queda plegada: el usuario normal no la abre
        // nunca y soporte la necesita siempre.
        crudo: crudoDelIntento(intento),
      }
    })
  }, [intentos.data])

  const acuse = entrega === null ? null : textoAcuse(entrega.acuseEstado)
  const motivo = entrega === null ? null : textoMotivoAcuse(entrega.acuseMotivo)

  return (
    <PanelLateral
      abierto={entrega !== null}
      onCerrar={onCerrar}
      titulo={entrega === null ? 'Entrega' : nombreReceptor(entrega)}
      subtitulo={entrega === null ? undefined : <ENCF valor={entrega.eNCF} />}
      pie={
        entrega === null ? undefined : (
          <>
            <Link to="/documentos" search={{ documento: entrega.documentoId }}>
              <Boton variante="secundario">Ver el documento</Boton>
            </Link>
            {/*
              Reintentar solo donde tiene sentido. Sobre un rechazo no aparece:
              el receptor entendió el comprobante y dijo que no, y repetir el
              mismo XML produce el mismo rechazo.
            */}
            {puedeReintentar ? (
              <Boton
                variante="primario"
                icono={<RefreshCw />}
                disabled={reintentar.isPending}
                onClick={() => reintentar.mutate(entrega.documentoId)}
              >
                {reintentar.isPending ? 'Encolando…' : 'Reintentar la entrega'}
              </Boton>
            ) : null}
          </>
        )
      }
    >
      {entrega === null || presentacion === null ? (
        <div className="flex flex-col gap-2">
          <LineaEsqueleto ancho="w-2/3" />
          <LineaEsqueleto ancho="w-1/2" />
          <LineaEsqueleto ancho="w-5/6" />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/*
            El desenlace con todas las letras y la salida que le corresponde.
            Para `no_entregable` esto sale en gris y dice que es lo correcto:
            es el mensaje que evita la llamada a soporte de "¿por qué no se le
            entregó a mi cliente?".
          */}
          <Banner tono={presentacion.tono} titulo={presentacion.etiqueta}>
            {presentacion.explicacion} {TEXTO_ACCION[presentacion.accion]}
          </Banner>

          {/* La respuesta del propio reencolado, que puede ser un "no". */}
          {reintentar.isSuccess && reintentar.data !== undefined ? (
            <Banner
              tono={reintentar.data.encolada ? 'azul' : 'ambar'}
              titulo={
                reintentar.data.encolada
                  ? 'Entrega encolada.'
                  : 'No se encoló, y el servidor dice por qué.'
              }
            >
              {reintentar.data.encolada
                ? 'Se reintenta sola. La fila cambia cuando el receptor conteste.'
                : (reintentar.data.motivo ?? 'El servidor no dio motivo.')}
            </Banner>
          ) : null}

          {reintentar.isError ? (
            <Banner tono="rojo" titulo={traducirErrorApi(reintentar.error).titulo}>
              {traducirErrorApi(reintentar.error).contexto}
            </Banner>
          ) : null}

          <dl className="flex flex-col">
            <Dato etiqueta="Desenlace">
              <InsigniaEstado tono={presentacion.tono} aclaracion={presentacion.aclaracion}>
                {presentacion.etiqueta}
              </InsigniaEstado>
            </Dato>
            <Dato etiqueta="Tipo">
              {nombreTipoECF(tipoComoTexto(entrega.tipo))}
            </Dato>
            <Dato etiqueta="Canal">
              <span title={PRESENTACION_CANAL[entrega.canal].nota}>
                {etiquetaCanal(entrega.canal)}
              </span>
              {/* Que un canal sea cortesía comercial y no cumpla la obligación
                  legal es justo lo que no se puede dar por sabido. */}
              {!PRESENTACION_CANAL[entrega.canal].cumpleObligacion ? (
                <span className="ml-2">
                  <InsigniaDiscreta>no cumple la obligación</InsigniaDiscreta>
                </span>
              ) : null}
            </Dato>
            <Dato etiqueta="RNC del receptor">
              <RNC valor={entrega.rncReceptor} />
            </Dato>
            {entrega.destino !== null ? (
              <Dato etiqueta="Destino">
                {/* Congelado: el directorio cambia, lo que se intentó no. */}
                <span className="mono block truncate text-etiqueta text-gris-600" title={entrega.destino}>
                  {entrega.destino}
                </span>
              </Dato>
            ) : null}
            <Dato etiqueta="Nº de intento">
              <span className="numero">{entrega.intento}</span>
            </Dato>
            {entrega.httpStatus !== null ? (
              <Dato etiqueta="Respuesta HTTP">
                <span className="numero">{entrega.httpStatus}</span>
              </Dato>
            ) : null}
            <Dato etiqueta="Intentada el">
              <FechaHora valor={entrega.creadoEl} />
            </Dato>
            {entrega.entregadoEl !== null ? (
              <Dato etiqueta="Entregada el">
                <FechaHora valor={entrega.entregadoEl} />
              </Dato>
            ) : null}
          </dl>

          {/* El acuse del receptor -------------------------------------- */}
          <div className="flex flex-col gap-1">
            <h3 className="text-seccion text-gris-900">Acuse del receptor</h3>
            {acuse === null ? (
              <p className="text-cuerpo text-gris-600">
                El receptor no llegó a contestar un acuse. No es un "no recibido": es que no hubo
                respuesta.
              </p>
            ) : (
              <>
                {/*
                  El estado del ARECF va invertido respecto del e-CF: el cero es
                  "recibido" y es el bueno. Aquí no se compara contra un literal
                  suelto, se pregunta a `textoAcuse`.
                */}
                <InsigniaEstado tono={tonoDelAcuse(entrega.acuseEstado)}>{acuse}</InsigniaEstado>
                {motivo !== null ? (
                  <p className="text-cuerpo text-gris-700">
                    {motivo}.{' '}
                    <span className="text-gris-600">{salidaMotivoAcuse(entrega.acuseMotivo)}</span>
                  </p>
                ) : null}
              </>
            )}
            {entrega.mensaje !== null ? (
              <p className="text-etiqueta text-gris-600">{entrega.mensaje}</p>
            ) : null}
          </div>

          {/* Todos los intentos ---------------------------------------- */}
          <div className="flex flex-col gap-2">
            <h3 className="text-seccion text-gris-900">Los intentos de este documento</h3>
            {intentos.isLoading ? (
              <LineaEsqueleto ancho="w-1/2" />
            ) : eventos.length === 0 ? (
              <p className="text-cuerpo text-gris-600">Sin más intentos registrados.</p>
            ) : (
              <LineaDeTiempo eventos={eventos} />
            )}
          </div>
        </div>
      )}
    </PanelLateral>
  )
}

/** Lo técnico de un intento, para el pliegue de soporte. */
function crudoDelIntento(intento: FilaEntrega): string | undefined {
  const partes = [
    `canal: ${intento.canal}`,
    `estado: ${intento.estado}`,
    intento.httpStatus === null ? null : `http: ${intento.httpStatus}`,
    intento.acuseEstado === null ? null : `acuseEstado: ${intento.acuseEstado}`,
    intento.acuseMotivo === null ? null : `acuseMotivo: ${intento.acuseMotivo}`,
    intento.destino === null ? null : `destino: ${intento.destino}`,
    intento.mensaje === null ? null : `mensaje: ${intento.mensaje}`,
  ].filter((parte): parte is string => parte !== null)

  return partes.length === 0 ? undefined : partes.join('\n')
}
